import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Animated, Easing } from 'react-native';

interface SkeletonProps {
  count?: number;
}

const UserTableSkeleton: React.FC<SkeletonProps> = ({ count = 2 }) => {
  const animatedValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(animatedValue, {
        toValue: 1,
        duration: 1500,
        easing: Easing.ease,
        useNativeDriver: false
      })
    );
    animation.start();
    return () => animation.stop();
  }, [animatedValue]);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.3, 0.7, 0.3]
  });

  return (
    <View style={styles.container}>
      <ScrollView>
        <View style={styles.table}>
          {Array(count)
            .fill(0)
            .map((_, index) => (
              <View key={index} style={styles.tableRow}>
                <View
                  style={{
                    ...styles.cell,
                    flex: 0.5,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  <View style={[styles.skeletonCheckbox, { opacity }]} />
                </View>
                <View style={[styles.skeletonCell, { flex: 1, opacity }]} />
                <View style={[styles.skeletonCell, { flex: 1, opacity }]} />
                <View style={[styles.skeletonCell, { flex: 3, opacity }]} />
                <View style={[styles.skeletonCell, { flex: 1, opacity }]} />
              </View>
            ))}
        </View>
      </ScrollView>
    </View>
  );
};

const VerificationTableSkeleton: React.FC<SkeletonProps> = ({ count = 5 }) => {
  const animatedValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(animatedValue, {
        toValue: 1,
        duration: 1500,
        easing: Easing.ease,
        useNativeDriver: false
      })
    );
    animation.start();
    return () => animation.stop();
  }, [animatedValue]);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.3, 0.7, 0.3]
  });

  return (
    <View style={styles.container}>
      <ScrollView>
        <View style={styles.table}>
          {Array(count)
            .fill(0)
            .map((_, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.skeletonCell, { opacity }]} />
                <View style={[styles.skeletonCell, { opacity }]} />
                <View style={[styles.skeletonCell, { opacity }]} />
                <View style={[styles.skeletonCell, { opacity }]} />
              </View>
            ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 16,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1a1a1a'
  },
  table: {
    width: '100%',
    backgroundColor: '#ffffff'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderBottomWidth: 2,
    borderBottomColor: '#E5E7EB',
    borderRadius: 6,
    marginBottom: 4
  },
  headerCell: {
    flex: 1,
    padding: 12,
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'left'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#ffffff',
    paddingVertical: 4
  },
  cell: {
    flex: 2,
    padding: 12,
    fontSize: 14,
    color: '#4B5563',
    textAlign: 'left'
  },
  skeletonCell: {
    height: 20,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    margin: 12
  },
  skeletonButton: {
    height: 40,
    backgroundColor: '#E5E7EB',
    borderRadius: 6,
    width: 150
  },
  skeletonText: {
    height: 20,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    marginRight: 10
  },
  skeletonCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: '#E5E7EB'
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginTop: 8
  },
  pageInfo: {
    color: '#6B7280',
    marginHorizontal: 16,
    fontSize: 14
  }
});

export { UserTableSkeleton, VerificationTableSkeleton };
